import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  useTheme,
} from '@mui/material';
import {
  HealthAndSafety as HealthIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  Support as SupportIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { PageLayout } from '../../components/footer/PageLayout';
import { ContentSection, FeatureCard } from '../../components/footer/ContentSection';
import { CTASection } from '../../components/footer/CTASection';

export const InsurancePage: React.FC = () => {
  const theme = useTheme();

  const insuranceFeatures = [
    {
      icon: <HealthIcon sx={{ fontSize: 40 }} />,
      title: "Major Insurance Accepted",
      description: "We accept most major insurance plans including Medicare, Medicaid, and private insurance providers.",
      action: <Button variant="outlined" href="/contact">Check Coverage</Button>,
    },
    {
      icon: <CreditCardIcon sx={{ fontSize: 40 }} />,
      title: "Flexible Payment Options",
      description: "Pay with insurance, HSA/FSA cards, or out-of-pocket. We offer payment plans for qualifying patients.",
      action: <Button variant="outlined" href="/payment-options">View Options</Button>,
    },
    {
      icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
      title: "Transparent Billing",
      description: "No surprise charges. See exactly what you'll pay before your appointment with clear, upfront pricing.",
      action: <Button variant="outlined" href="/pricing">View Pricing</Button>,
    },
    {
      icon: <SupportIcon sx={{ fontSize: 40 }} />,
      title: "Insurance Support",
      description: "Our team helps you understand your coverage and handles insurance paperwork on your behalf.",
      action: <Button variant="outlined" href="/contact">Get Help</Button>,
    },
  ];

  const acceptedInsurance = [
    "Medicare",
    "Medicaid",
    "Blue Cross Blue Shield",
    "Aetna",
    "Cigna",
    "UnitedHealthcare",
    "Humana",
    "Kaiser Permanente",
    "Anthem",
    "Molina Healthcare",
  ];

  const paymentMethods = [
    {
      method: "Insurance",
      description: "Most major insurance plans accepted",
      icon: "🏥",
    },
    {
      method: "HSA/FSA",
      description: "Health Savings and Flexible Spending Accounts",
      icon: "💳",
    },
    {
      method: "Credit/Debit",
      description: "All major credit and debit cards accepted",
      icon: "💳",
    },
    {
      method: "Payment Plans",
      description: "Flexible payment plans available",
      icon: "📅",
    },
  ];

  const faqItems = [
    {
      question: "How do I know if my insurance is accepted?",
      answer: "You can check your insurance coverage by contacting our support team or using our online verification tool. We accept most major insurance plans and will verify your benefits before your first appointment.",
    },
    {
      question: "What if my insurance doesn't cover the service?",
      answer: "If your insurance doesn't cover the service, we offer competitive self-pay rates and flexible payment plans. Our team will work with you to find an affordable solution.",
    },
    {
      question: "Do I need a referral from my doctor?",
      answer: "This depends on your insurance plan. Some plans require a referral for certain services, while others allow direct access. We can help you determine if a referral is needed.",
    },
    {
      question: "How does billing work?",
      answer: "We handle all insurance billing on your behalf. You'll only be responsible for any copays, deductibles, or coinsurance as determined by your insurance plan. We'll provide clear explanations of all charges.",
    },
    {
      question: "Can I use my HSA or FSA card?",
      answer: "Yes! Our services are typically eligible for HSA and FSA reimbursement. You can use your HSA/FSA card directly or pay out-of-pocket and submit for reimbursement.",
    },
  ];

  return (
    <PageLayout
      title="Insurance Coverage & Payment"
      subtitle="Understanding your insurance coverage and payment options for quality healthcare services."
      breadcrumbs={[
        { label: "For Patients", href: "/for-patients" },
        { label: "Insurance Coverage" },
      ]}
    >
      {/* Insurance Overview */}
      <ContentSection
        title="Insurance & Payment Made Simple"
        subtitle="We work with most major insurance providers and offer flexible payment options to make quality healthcare accessible and affordable."
        backgroundColor={theme.palette.grey[50]}
      >
        <Grid container spacing={4}>
          {insuranceFeatures.map((feature, index) => (
            <Grid item xs={12} md={6} lg={3} key={index}>
              <FeatureCard {...feature} />
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* Accepted Insurance */}
      <ContentSection
        title="Accepted Insurance Plans"
        subtitle="We accept most major insurance plans. Don't see your plan listed? Contact us to verify coverage."
      >
        <Grid container spacing={2}>
          {acceptedInsurance.map((insurance, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Box
                sx={{
                  p: 2,
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 2,
                  textAlign: 'center',
                  backgroundColor: theme.palette.grey[50],
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.contrastText,
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {insurance}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* Payment Methods */}
      <ContentSection
        title="Payment Options"
        subtitle="Choose the payment method that works best for you."
        backgroundColor={theme.palette.grey[50]}
      >
        <Grid container spacing={4}>
          {paymentMethods.map((method, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    fontSize: '3rem',
                    mb: 2,
                  }}
                >
                  {method.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    color: theme.palette.text.primary,
                  }}
                >
                  {method.method}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  {method.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* How It Works */}
      <ContentSection
        title="How Insurance Billing Works"
        subtitle="Our streamlined process makes insurance billing simple and transparent."
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Before Your Appointment
              </Typography>
              <Box component="ul" sx={{ pl: 0, listStyle: 'none' }}>
                {[
                  "Verify your insurance coverage",
                  "Confirm your benefits and copay amount",
                  "Schedule your appointment",
                  "Receive confirmation with cost breakdown",
                ].map((item, index) => (
                  <Box component="li" key={index} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                    <CheckIcon sx={{ color: theme.palette.success.main, mr: 2, mt: 0.5 }} />
                    <Typography variant="body1">{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                After Your Appointment
              </Typography>
              <Box component="ul" sx={{ pl: 0, listStyle: 'none' }}>
                {[
                  "We submit claims to your insurance",
                  "Insurance processes your claim",
                  "You receive explanation of benefits",
                  "Pay only your portion (copay/deductible)",
                ].map((item, index) => (
                  <Box component="li" key={index} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                    <CheckIcon sx={{ color: theme.palette.success.main, mr: 2, mt: 0.5 }} />
                    <Typography variant="body1">{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </ContentSection>

      {/* FAQ */}
      <ContentSection
        title="Frequently Asked Questions"
        subtitle="Common questions about insurance coverage and payment options."
        backgroundColor={theme.palette.grey[50]}
      >
        <Grid container spacing={3}>
          {faqItems.map((faq, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Box
                sx={{
                  p: 3,
                  backgroundColor: 'white',
                  borderRadius: 2,
                  boxShadow: theme.shadows[2],
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    color: theme.palette.primary.main,
                  }}
                >
                  {faq.question}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  {faq.answer}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* Important Information */}
      <ContentSection
        title="Important Information"
        subtitle="Please read this important information about insurance coverage and payment."
      >
        <Box
          sx={{
            p: 3,
            border: `2px solid ${theme.palette.info.main}`,
            borderRadius: 2,
            backgroundColor: theme.palette.info.light,
            color: theme.palette.info.contrastText,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <InfoIcon sx={{ fontSize: 24, mt: 0.5 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                Insurance Coverage Disclaimer
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                While we accept most major insurance plans, coverage varies by plan and individual circumstances. 
                We recommend verifying your benefits before your first appointment. We are not responsible for 
                insurance denials or changes in coverage.
              </Typography>
              <Typography variant="body1">
                For questions about your specific coverage, please contact your insurance provider directly 
                or reach out to our support team for assistance.
              </Typography>
            </Box>
          </Box>
        </Box>
      </ContentSection>

      {/* CTA Section */}
      <CTASection
        title="Ready to Check Your Coverage?"
        subtitle="Contact us today to verify your insurance benefits and schedule your first appointment."
        primaryButton={{
          text: "Check Coverage",
          href: "/contact",
        }}
        secondaryButton={{
          text: "Schedule Appointment",
          href: "/search",
          variant: "outlined",
        }}
      />
    </PageLayout>
  );
};
